import { computed, ref, watch } from 'vue';
import { camelCase } from '@replygirl/change-case-object';
import { Api } from '@/Api';
import { useToast } from '@/composables/useToast';

interface CalendarConnection {
  platform: 'google' | 'microsoft';
  connected: boolean;
}

export const useMeetingsIntegration = () => {
  const toast = useToast();

  const calendarConnections = ref<CalendarConnection[]>([]);
  const localConnectionId = ref<number | null>(null);
  const busy = ref(false);
  const calendarsLoading = ref(true);
  const autoRecord = ref(false);
  const prefsHydrated = ref(false);

  const googleConnection = computed(() => calendarConnections.value.find((c: CalendarConnection) => c.platform === 'google' && c.connected));
  const hasAnyCalendar = computed(() => calendarConnections.value.some((c: CalendarConnection) => c.connected));

  const loadCalendars = async () => {
    calendarsLoading.value = true;
    try {
      const response = (await Api.recall.calendar.list()) as {
        data?: {
          calendarConnection?: {
            id?: number | string | null;
            attributes?: { calendars?: unknown[] };
          };
        };
      };
      const connection = response?.data?.calendarConnection;
      localConnectionId.value = connection?.id ? Number(connection.id) : null;
      const list = (connection?.attributes?.calendars ?? []) as Record<string, unknown>[];
      calendarConnections.value = list.map(c => camelCase(c) as CalendarConnection);
    } catch {
      calendarConnections.value = [];
      localConnectionId.value = null;
    } finally {
      calendarsLoading.value = false;
    }
  };

  const loadPrefs = async () => {
    // Fetch fresh user data so the toggle reflects whatever's saved server-side.
    // ActiveRecord resources serialize as data.<resource>.attributes (no extra
    // data wrapper); parseResponse has already camelCased the keys.
    try {
      const response = (await Api.user.currentUser()) as {
        data?: { user?: { attributes?: { meetings?: { autoRecord?: boolean } } } };
      };
      const meetings = response?.data?.user?.attributes?.meetings ?? {};
      autoRecord.value = !!meetings.autoRecord;
    } catch {
      autoRecord.value = false;
    }
  };

  const connectCalendar = async (provider: 'google' | 'outlook') => {
    busy.value = true;
    try {
      const response = (await Api.recall.calendar.create(provider)) as {
        data?: { oauthUrl?: { attributes?: string } };
      };
      const oauthUrl = response?.data?.oauthUrl?.attributes;
      if (oauthUrl) {
        window.location.href = oauthUrl;
      } else {
        toast.error('Could not start calendar connection.');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to start calendar connection.');
    } finally {
      busy.value = false;
    }
  };

  const disconnect = async () => {
    if (!localConnectionId.value) return;
    busy.value = true;
    try {
      await Api.recall.calendar.disconnect(localConnectionId.value);
      await loadCalendars();
      toast.success('Calendar disconnected.');
    } finally {
      busy.value = false;
    }
  };

  const persist = async () => {
    try {
      await Api.userPreferences.updateMeetings({ autoRecord: autoRecord.value });
      toast.success(autoRecord.value ? 'Auto-record enabled.' : 'Auto-record disabled.');
    } catch {
      toast.error('Failed to save settings.');
    }
  };

  // Watch autoRecord and persist on user-driven changes. The gate flips ON after
  // hydration finishes loading the saved value, so hydration doesn't trigger a
  // no-op PATCH back to the server.
  watch(autoRecord, () => {
    if (prefsHydrated.value) persist();
  });

  const hydrate = async () => {
    await Promise.all([loadPrefs(), loadCalendars()]);
    prefsHydrated.value = true;
  };

  return {
    autoRecord,
    busy,
    calendarsLoading,
    connectCalendar,
    disconnect,
    googleConnection,
    hasAnyCalendar,
    hydrate,
  };
};
